import { useState } from 'react'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { credits, paymentReads } from '../../api/fee'
import type { CreditTransaction } from '../../api/types'
import { BuyCredit } from '../../components/BuyCredit'
import { PageTitle, QueryState, Tabs } from '../../components/common'
import { BillingInfoBlock, PaymentMethodInfoBlock } from '../../components/payment'
import { useProject } from '../../layout/projectContext'
import { CREDIT_CAUSE_LABELS, CREDIT_REFERENCE_LABELS } from '../../lib/catalogue'
import { formatCredit, formatDateTime } from '../../lib/format'
import { hasMethod } from '../../lib/paymentHelpers'

/**
 * Port of `PaymentCreditViewComponent` (`projects/:projectId/payment/credit`):
 * the caller's `credit` balance (the only purchasable currency, 1 credit =
 * USD 1), read-only balances of every other currency, "Buy credit", and the
 * per-currency credit history.
 */
export function PaymentCreditPage() {
  const { projectId } = useProject()
  const q = useApiQuery(() => credits.search(projectId), [projectId])
  const pm = useApiQuery(() => paymentReads.method(projectId), [projectId])
  const billing = useApiQuery(() => paymentReads.billing(projectId), [projectId])
  const [buying, setBuying] = useState(false)
  const infos = q.data ?? []
  const main = infos.find((c) => c.currency === 'credit')
  const others = infos.filter((c) => c.currency !== 'credit')
  const [tab, setTab] = useState('credit')
  const history = infos.find((c) => c.currency === tab) ?? main
  const balance = Number(main?.balance ?? 0)

  const columns: Column<CreditTransaction & { idx: number }>[] = [
    { key: 'idx', label: '#', align: 'right', numeric: true },
    { key: 'createdAt', label: 'Date', format: (v) => formatDateTime(v) },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      numeric: true,
      format: (v, r) => formatCredit(v as number, r.currency),
    },
    { key: 'result', label: 'Result', format: (v) => (Number(v) === 0 ? 'SUCCESS' : `Error code #${String(v)}`) },
    {
      key: 'balanceAfter',
      label: 'Balance after transaction',
      align: 'right',
      numeric: true,
      format: (v, r) => formatCredit(v as number, r.currency),
    },
    { key: 'cause', label: 'Cause', format: (v) => CREDIT_CAUSE_LABELS[String(v)] ?? String(v ?? '') },
    { key: 'reference', label: 'Reference', format: (v) => CREDIT_REFERENCE_LABELS[String(v)] ?? String(v ?? '') },
  ]

  return (
    <div className="portal-card">
      <PageTitle title="Credits" />
      <QueryState loading={q.loading} error={q.error}>
        <div className="portal-grid-2">
          <div>
            <div className={`fs-panel ${balance > 0 ? 'fs-panel--success' : balance < 0 ? 'fs-panel--danger' : 'fs-panel--warning'}`}>
              <h3>Credit balance</h3>
              <p className="fs-amount" data-testid="credit-balance">
                {formatCredit(balance, 'credit')} credit{Math.abs(balance) === 1 ? '' : 's'}
              </p>
              <button type="button" onClick={() => setBuying(!buying)} aria-expanded={buying}>
                Buy credit
              </button>
            </div>
            {others.map((c) => (
              <div key={c.currency} className="fs-panel">
                <h3>{c.currency}</h3>
                <p className="fs-amount">{formatCredit(c.balance, c.currency)}</p>
              </div>
            ))}
            {buying && (
              <>
                <PaymentMethodInfoBlock projectId={projectId} pm={pm.data} />
                <BillingInfoBlock projectId={projectId} billing={billing.data} />
                <BuyCredit
                  projectId={projectId}
                  hasPaymentMethod={hasMethod(pm.data)}
                  onCharged={() => {
                    q.refetch()
                    pm.refetch()
                  }}
                />
              </>
            )}
          </div>
          <div>
            <h2>Credit history</h2>
            {infos.length > 0 && (
              <Tabs tabs={infos.map((c) => ({ id: c.currency, label: c.currency }))} active={history?.currency ?? tab} onChange={setTab} />
            )}
            <div className="portal-table-scroll">
              <Table
                columns={columns}
                rows={(history?.transactions ?? []).map((t, i) => ({ ...t, idx: i + 1 }))}
                rowKey={(r) => r.id || r.idx}
              />
            </div>
            {infos.length === 0 && <p className="portal-text-muted">No credit yet.</p>}
          </div>
        </div>
      </QueryState>
    </div>
  )
}
